import { UserType } from "@/interfaces";
import { DEFAULT_CACHE_TIME } from "@/utils/constants";
import { useQuery } from "@tanstack/react-query";
import type { SortConfig } from "@/components/DataTable/types";
import api from "./api";
import { serializeFilterParams } from "./queryParams";

/**
 * Create Vendor User request payload
 */
export interface CreateVendorUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  designation?: string | null;
  linkedinId?: string | null;
  isPrimary?: boolean;
  isActive?: boolean;
  companyId?: string; // Company to map this vendor user to
  userId?: string; // Existing user ID if creating additional mapping
}

/**
 * Update Vendor User request payload
 */
export type UpdateVendorUserRequest = Partial<CreateVendorUserRequest>;

/**
 * Vendor User list response from API
 */
export interface VendorUserListResponse {
  data: UserType[];
  message?: string;
  total?: number;
  page?: number;
  limit?: number;
}

export interface VendorUserStatsResponse {
  totalUsers: number;
  newUsers: number;
  activeUsers: number;
}

/**
 * Vendor User service for API calls
 */
const vendorUserService = {
  /**
   * Create new User for a vendor
   */
  create: async (vendorId: string, data: CreateVendorUserRequest) => {
    const resp = await api.post<{ data: UserType; message?: string }>(`/vendors/${vendorId}/users`, data);
    return resp.data;
  },

  /**
   * Get all Users for a vendor
   */
  getAll: async (vendorId: string, includeInactive: boolean = false) => {
    const params = includeInactive ? "?includeInactive=true" : "";
    const resp = await api.get<VendorUserListResponse>(`/vendors/${vendorId}/users${params}`);
    return resp.data;
  },

  /**
   * Get a specific User by ID
   */
  getById: async (vendorId: string, userId: string) => {
    const resp = await api.get<{ data: UserType; message?: string }>(`/vendors/${vendorId}/users/${userId}`);
    return resp.data;
  },

  /**
   * Update a User
   */
  update: async (vendorId: string, userId: string, data: UpdateVendorUserRequest) => {
    const resp = await api.put<{ data: UserType; message?: string }>(`/vendors/${vendorId}/users/${userId}`, data);
    return resp.data;
  },

  /**
   * Delete a User
   */
  delete: async (vendorId: string, userId: string) => {
    const resp = await api.delete<{ data: boolean; message?: string }>(`/vendors/${vendorId}/users/${userId}`);
    return resp.data;
  },

  /**
   * Get all vendor users by company ID (optimized single call)
   */
  getAllByCompanyId: async (companyId: string, includeInactive: boolean = false) => {
    const params = includeInactive ? "?includeInactive=true" : "";
    const resp = await api.get<VendorUserListResponse>(`/vendors/users/company/${companyId}${params}`);
    return resp.data;
  },

  getPaginatedByCompanyId: async (
    companyId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      sort?: SortConfig;
      filters?: Record<string, unknown>;
      signal?: AbortSignal;
    },
  ) => {
    const { page = 1, limit = 20, search, sort, filters, signal } = params || {};
    const serializedFilters = serializeFilterParams(filters);
    const resp = await api.get<VendorUserListResponse>(`/vendors/users/company/${companyId}`, {
      params: {
        page,
        limit,
        search,
        sortField: sort?.field,
        sortOrder: sort?.order,
        ...serializedFilters,
      },
      signal,
    });
    return resp.data;
  },

  getByCompanyIdStats: async (companyId: string) => {
    const resp = await api.get<VendorUserStatsResponse>(`/vendors/users/company/${companyId}/stats`);
    return resp.data;
  },

  getByCompanyIdsStats: async (companyIds: string[]) => {
    const resp = await api.get<VendorUserStatsResponse>("/vendors/users/company/stats", {
      params: { companyIds: companyIds.join(",") },
    });
    return resp.data;
  },
};

/**
 * React Query hook to fetch all vendor users by company ID
 */
export const useGetVendorUsersByCompanyId = (companyId: string | undefined, includeInactive: boolean = false) => {
  return useQuery({
    queryKey: ["vendorUsers", "company", companyId, includeInactive],
    queryFn: () => vendorUserService.getAllByCompanyId(companyId!, includeInactive),
    enabled: !!companyId,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

export const useGetVendorUsersByCompanyStats = (companyId?: string) => {
  return useQuery<VendorUserStatsResponse>({
    queryKey: ["vendorUsers", "company", companyId, "stats"],
    queryFn: () => vendorUserService.getByCompanyIdStats(companyId!),
    enabled: !!companyId,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

export const useGetVendorUsersByCompaniesStats = (companyIds: string[]) => {
  return useQuery<VendorUserStatsResponse>({
    queryKey: ["vendorUsers", "companies", companyIds, "stats"],
    queryFn: () => vendorUserService.getByCompanyIdsStats(companyIds),
    enabled: companyIds.length > 0,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

export default vendorUserService;
